/**
 * Team page: pin description, scrub dim → white per character (SplitText + ScrollTrigger).
 * Hides header title when description block intersects it in the viewport.
 */

(function() {
	'use strict';

	var DIM = 'rgba(255, 255, 255, 0.3)';
	var LIT = '#ffffff';

	var TITLE_HIDDEN_CLASS = 'page-team__header-title--hidden-by-description';

	// Map ScrollTrigger progress so characters reach full white before progress === 1,
	// leaving the rest of the scroll range as a visual hold while pin continues.
	var DESC_FILL_END = 0.4;

	var initAttempts = 0;
	var maxInitAttempts = 50;

	var titleOverlapBound = false;
	var titleOverlapRequestTick = function() {};

	function getScrollTrigger() {
		if (typeof gsap !== 'undefined' && gsap.ScrollTrigger) {
			return gsap.ScrollTrigger;
		}
		if (typeof window !== 'undefined' && window.ScrollTrigger) {
			return window.ScrollTrigger;
		}
		if (typeof ScrollTrigger !== 'undefined') {
			return ScrollTrigger;
		}
		if (typeof gsap !== 'undefined' && gsap.plugins && gsap.plugins.ScrollTrigger) {
			return gsap.plugins.ScrollTrigger;
		}
		return null;
	}

	function getSplitText() {
		if (typeof window !== 'undefined' && window.SplitText) {
			return window.SplitText;
		}
		if (typeof SplitText !== 'undefined') {
			return SplitText;
		}
		return null;
	}

	function prefersReducedMotion() {
		return typeof window !== 'undefined' &&
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	function rectsOverlap(a, b) {
		return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
	}

	/**
	 * Idempotent: first successful bind attaches scroll/resize; later calls return same requestTick.
	 */
	function bindTeamTitleOverlapOnce(scene) {
		if (titleOverlapBound) {
			return titleOverlapRequestTick;
		}

		var pageRoot = scene.closest ? scene.closest('.page-team') : null;
		var titleEl = pageRoot
			? pageRoot.querySelector('.page-team__header-title')
			: document.querySelector('.page-team__header-title');
		var descEl = scene.querySelector('.page-team__header-description');

		if (!titleEl || !descEl) {
			return function() {};
		}

		titleOverlapBound = true;

		var scheduled = false;

		function syncTitleOverlap() {
			var tr = titleEl.getBoundingClientRect();
			var dr = descEl.getBoundingClientRect();
			var overlap = rectsOverlap(tr, dr);

			if (overlap) {
				titleEl.classList.add(TITLE_HIDDEN_CLASS);
				titleEl.setAttribute('aria-hidden', 'true');
			} else {
				titleEl.classList.remove(TITLE_HIDDEN_CLASS);
				titleEl.removeAttribute('aria-hidden');
			}
		}

		function requestTick() {
			if (!scheduled) {
				scheduled = true;
				requestAnimationFrame(function() {
					scheduled = false;
					syncTitleOverlap();
				});
			}
		}

		window.addEventListener('scroll', requestTick, { passive: true });
		window.addEventListener('resize', requestTick);
		requestTick();

		titleOverlapRequestTick = requestTick;
		return requestTick;
	}

	function applyCharColors(chars, progress) {
		var len = chars.length;
		if (!len) {
			return;
		}
		var t = progress * len;
		for (var i = 0; i < len; i++) {
			chars[i].style.color = i < t ? LIT : DIM;
		}
	}

	function fillProgressFromScrollProgress(scrollProgress) {
		if (scrollProgress <= 0) {
			return 0;
		}
		if (scrollProgress >= 1) {
			return 1;
		}
		var scaled = scrollProgress / DESC_FILL_END;
		return scaled > 1 ? 1 : scaled;
	}

	function initTeamDescriptionScroll() {
		var scene = document.querySelector('[data-team-description-scene]');
		var pinEl = document.querySelector('[data-team-description-pin]');
		var textEl = document.querySelector('[data-team-description-text]');

		if (!scene || !pinEl || !textEl) {
			return;
		}

		var requestTitleOverlapTick = bindTeamTitleOverlapOnce(scene);

		if (prefersReducedMotion()) {
			textEl.style.color = LIT;
			return;
		}

		if (typeof gsap === 'undefined') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initTeamDescriptionScroll, 100);
			}
			return;
		}

		var ScrollTriggerPlugin = getScrollTrigger();
		if (!ScrollTriggerPlugin || typeof ScrollTriggerPlugin.create !== 'function') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initTeamDescriptionScroll, 100);
			}
			return;
		}

		var SplitTextCtor = getSplitText();
		if (!SplitTextCtor || typeof SplitTextCtor.create !== 'function') {
			initAttempts++;
			if (initAttempts < maxInitAttempts) {
				setTimeout(initTeamDescriptionScroll, 100);
			}
			return;
		}

		gsap.registerPlugin(ScrollTriggerPlugin);

		var split = SplitTextCtor.create(textEl, {
			type: 'chars',
			charsClass: 'page-team__description-char',
			aria: 'auto',
		});

		var chars = split.chars || [];
		applyCharColors(chars, 0);

		function setFillFromProgress(self) {
			applyCharColors(split.chars, fillProgressFromScrollProgress(self.progress));
		}

		var st = ScrollTriggerPlugin.create({
			trigger: scene,
			start: 'top top',
			end: 'bottom bottom',
			pin: pinEl,
			scrub: 0.35,
			invalidateOnRefresh: true,
			onUpdate: function(self) {
				setFillFromProgress(self);
				requestTitleOverlapTick();
			},
			onRefresh: function(self) {
				chars = split.chars;
				setFillFromProgress(self);
				requestTitleOverlapTick();
			},
			onLeave: function() {
				applyCharColors(split.chars, 1);
				requestTitleOverlapTick();
			},
			onLeaveBack: function() {
				applyCharColors(split.chars, 0);
				requestTitleOverlapTick();
			},
		});

		requestAnimationFrame(function() {
			if (st && typeof st.progress === 'number') {
				chars = split.chars;
				setFillFromProgress(st);
			}
			requestTitleOverlapTick();
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTeamDescriptionScroll);
	} else {
		initTeamDescriptionScroll();
	}
})();
